import { create } from 'zustand';
import logger from '../services/loggerService';
import { userApi } from '../services/api/userApi';

interface UIState {
  isDesktopSidebarCollapsed: boolean;
  isSidebarHovered: boolean;
  isMobileSidebarOpen: boolean;
  notificationCount: number;

  // Actions
  toggleDesktopSidebar: () => Promise<void>;
  setSidebarHovered: (isHovered: boolean) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (isOpen: boolean) => void;
  closeMobileSidebar: () => void;
  setNotificationCount: (count: number) => void;
  incrementNotificationCount: () => void;

  // Sidebar Resize
  sidebarWidth: number;
  setSidebarWidth: (width: number) => Promise<void>;

  isResizing: boolean;
  setIsResizing: (isResizing: boolean) => void;

  /**
   * Accordion state for sidebar grouped navigation.
   * Stored here (not in Sidebar component state) to persist
   * across Sidebar remounts during route navigation.
   */
  sidebarOpenSections: Record<string, boolean>;
  toggleSidebarSection: (name: string) => Promise<void>;
  setSidebarSectionOpen: (name: string, isOpen: boolean) => Promise<void>;

  // Layout Persistence
  activePageTitle: string;
  setActivePageTitle: (title: string) => void;

  // User-scoped layout state synchronization
  userId: string | null;
  initUIState: (userId: string | null, initialSettings?: Record<string, unknown>) => void;
}

const getStorageKey = (userId: string | null): string => {
  return userId ? `sp:ui:${userId}` : 'sp:ui:anonymous';
};

const saveToLocal = (userId: string | null, state: { isDesktopSidebarCollapsed: boolean; sidebarWidth: number; sidebarOpenSections: Record<string, boolean>; uiVersion?: number }) => {
  try {
    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    logger.error('Failed to save UI state to localStorage', error, 'useUIStateStore');
  }
};

let syncTimeoutId: NodeJS.Timeout | null = null;

const debouncedSyncPreferences = (userId: string, get: () => UIState) => {
  if (syncTimeoutId) {
    clearTimeout(syncTimeoutId);
  }
  syncTimeoutId = setTimeout(async () => {
    try {
      const state = get();
      await userApi.updatePreferences({
        uiSettings: {
          sidebarCollapsed: state.isDesktopSidebarCollapsed,
          sidebarWidth: state.sidebarWidth,
          sidebarOpenSections: state.sidebarOpenSections,
          uiVersion: 1, // Layout versioning metadata added here
        }
      });
      logger.debug(`Successfully synced UI layout state to database for user: ${userId}`, { context: 'useUIStateStore' });
    } catch (error) {
      logger.error(`Failed to sync UI layout state to database for user: ${userId}`, error, 'useUIStateStore');
    }
  }, 5000);
};

export const useUIStateStore = create<UIState>((set, get) => ({
  isDesktopSidebarCollapsed: false,
  isSidebarHovered: false,
  isMobileSidebarOpen: false,
  notificationCount: 0,
  sidebarWidth: 256,
  isResizing: false,
  sidebarOpenSections: {},
  activePageTitle: '',
  userId: null,

  initUIState: (userId, initialSettings) => {
    let collapsed = false;
    let width = 256;
    let openSections: Record<string, boolean> = {};
    let version = 1;

    const key = getStorageKey(userId);

    if (initialSettings) {
      version = typeof initialSettings.uiVersion === 'number' ? initialSettings.uiVersion : 1;
      collapsed = !!initialSettings.sidebarCollapsed;
      width = typeof initialSettings.sidebarWidth === 'number' ? initialSettings.sidebarWidth : 256;
      openSections = (initialSettings.sidebarOpenSections as Record<string, boolean>) || {};
      
      saveToLocal(userId, { isDesktopSidebarCollapsed: collapsed, sidebarWidth: width, sidebarOpenSections: openSections, uiVersion: version });
    } else {
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          collapsed = !!parsed.isDesktopSidebarCollapsed;
          width = typeof parsed.sidebarWidth === 'number' ? parsed.sidebarWidth : 256;
          openSections = parsed.sidebarOpenSections || {};
          version = typeof parsed.uiVersion === 'number' ? parsed.uiVersion : 1;
        } else {
          // Check for legacy migration of sidebarWidth
          const legacyWidth = localStorage.getItem('sidebarWidth');
          if (legacyWidth) {
            width = parseInt(legacyWidth, 10) || 256;
            try {
              localStorage.removeItem('sidebarWidth');
            } catch (e) {
              // Ignore cleanup error
            }
          }
          saveToLocal(userId, { isDesktopSidebarCollapsed: collapsed, sidebarWidth: width, sidebarOpenSections: openSections, uiVersion: version });
        }
      } catch (error) {
        logger.error('Failed to read UI state from localStorage', error, 'useUIStateStore.initUIState');
      }
    }

    set({ userId, isDesktopSidebarCollapsed: collapsed, sidebarWidth: width, sidebarOpenSections: openSections });
    logger.debug(`Initialized UI state for user: ${userId || 'anonymous'} (UI version: ${version})`, { context: 'useUIStateStore' });
  },

  toggleDesktopSidebar: async () => {
    set((state) => {
      const newCollapsed = !state.isDesktopSidebarCollapsed;
      const updated = {
        isDesktopSidebarCollapsed: newCollapsed,
        sidebarWidth: state.sidebarWidth,
        sidebarOpenSections: state.sidebarOpenSections,
      };
      saveToLocal(state.userId, updated);
      if (state.userId) {
        debouncedSyncPreferences(state.userId, get);
      }
      return { isDesktopSidebarCollapsed: newCollapsed };
    });
  },

  setSidebarHovered: (isHovered) => set({ isSidebarHovered: isHovered }),

  toggleMobileSidebar: () => set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen })),

  setMobileSidebarOpen: (isOpen) => set({ isMobileSidebarOpen: isOpen }),

  closeMobileSidebar: () => set({ isMobileSidebarOpen: false }),

  setNotificationCount: (count) => set({ notificationCount: count }),

  incrementNotificationCount: () =>
    set((state) => ({ notificationCount: state.notificationCount + 1 })),

  setSidebarWidth: async (width) => {
    set((state) => {
      const updated = {
        isDesktopSidebarCollapsed: state.isDesktopSidebarCollapsed,
        sidebarWidth: width,
        sidebarOpenSections: state.sidebarOpenSections,
      };
      saveToLocal(state.userId, updated);
      if (state.userId) {
        debouncedSyncPreferences(state.userId, get);
      }
      return { sidebarWidth: width };
    });
  },

  setIsResizing: (isResizing) => {
    if (typeof document !== 'undefined') {
      if (isResizing) {
        document.body.classList.add('is-resizing');
      } else {
        document.body.classList.remove('is-resizing');
      }
    }
    set({ isResizing });
  },

  toggleSidebarSection: async (name) => {
    set((state) => {
      const newOpenSections = {
        ...state.sidebarOpenSections,
        [name]: !state.sidebarOpenSections[name],
      };
      const updated = {
        isDesktopSidebarCollapsed: state.isDesktopSidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        sidebarOpenSections: newOpenSections,
      };
      saveToLocal(state.userId, updated);
      if (state.userId) {
        debouncedSyncPreferences(state.userId, get);
      }
      return { sidebarOpenSections: newOpenSections };
    });
  },

  setSidebarSectionOpen: async (name, isOpen) => {
    set((state) => {
      if (state.sidebarOpenSections[name] === isOpen) return {};
      const newOpenSections = {
        ...state.sidebarOpenSections,
        [name]: isOpen,
      };
      const updated = {
        isDesktopSidebarCollapsed: state.isDesktopSidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        sidebarOpenSections: newOpenSections,
      };
      saveToLocal(state.userId, updated);
      if (state.userId) {
        debouncedSyncPreferences(state.userId, get);
      }
      return { sidebarOpenSections: newOpenSections };
    });
  },

  setActivePageTitle: (title) => set({ activePageTitle: title }),
}));
